import React, {
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import PropTypes from "prop-types";
import { LinearProgress, Typography } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { makeStyles } from "@material-ui/core/styles";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import Modal from "../../../../components/Modal";
import Button from "../../../../components/Button";
import { ROUTE_PATHS } from "../../../../utils/PathsNames";
import history from "../../../../routes/History";
import {
  runSmartImport,
  retryFailedBatches,
  LlmConfigError
} from "../../../../utils/smartImport/importRunner";
import {
  isLlmConfigured,
  getLlmModelName
} from "../../../../utils/smartImport/llmClient";
import { toFormikFlashcards } from "../../../../utils/smartImport/schema";
import {
  IMPORT_LIMITS,
  releaseImages
} from "../../../../utils/documentParser";

const useStyles = makeStyles(() => ({
  fileLabel: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
    padding: "36px 16px",
    border: "2px dashed #c3cde8",
    borderRadius: "8px",
    cursor: "pointer",
    color: "#3f51b5",
    backgroundColor: "#f6f8ff",
    textAlign: "center",
    "&:hover": { backgroundColor: "#eef2ff" }
  },
  hint: {
    color: "#6b7280",
    fontSize: "0.85rem",
    marginTop: "12px",
    textAlign: "center"
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
    marginTop: "16px"
  },
  summaryList: {
    margin: "8px 0 0",
    paddingLeft: "20px"
  },
  progressText: {
    marginTop: "12px",
    color: "#374151"
  }
}));

const PHASES = {
  IDLE: "idle",
  RUNNING: "running",
  SUMMARY: "summary",
  ERROR: "error"
};

function SmartImportModal({ isOpen, handleClose }) {
  const classes = useStyles();
  const abortRef = useRef(null);

  const [phase, setPhase] = useState(PHASES.IDLE);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [configMissing, setConfigMissing] = useState(
    !isLlmConfigured()
  );
  // Live per-batch notices shown under the progress bar while running
  const [batchNotices, setBatchNotices] = useState([]);

  const pushBatchNotice = useCallback((p) => {
    if (!p?.message) return;
    setBatchNotices((prev) => [
      ...prev.slice(-4), // keep the last five; the list must stay small
      { batch: p.chunkIndex, message: p.message }
    ]);
  }, []);

  // Holds the images of a finished run until either the Add form takes
  // ownership or the result is discarded. On discard the preview object URLs
  // are revoked; once the form owns them it must not revoke, or previews break.
  const ownedImagesRef = useRef(null);

  const discardOwnedImages = useCallback(() => {
    if (ownedImagesRef.current) {
      releaseImages(ownedImagesRef.current);
      ownedImagesRef.current = null;
    }
  }, []);

  useEffect(() => {
    // reset whenever the modal (re)opens
    if (isOpen) {
      discardOwnedImages();
      setPhase(PHASES.IDLE);
      setProgress(null);
      setResult(null);
      setErrorMessage("");
      setSelectedFile(null);
      setBatchNotices([]);
      setConfigMissing(!isLlmConfigured());
    }
  }, [isOpen, discardOwnedImages]);

  useEffect(
    () => () => {
      // Unmounting with an unreviewed result would otherwise leak its URLs.
      discardOwnedImages();
    },
    [discardOwnedImages]
  );

  const abortRun = () => {
    abortRef.current?.abort();
    abortRef.current = null;
  };

  const handleModalClose = () => {
    abortRun();
    discardOwnedImages();
    handleClose();
  };

  const handleFilePicked = (event) => {
    const file = event.target.files?.[0];
    event.target.value = null;
    if (file) setSelectedFile(file);
  };

  const startImport = useCallback(async () => {
    if (!selectedFile) return;
    const controller = new AbortController();
    abortRef.current = controller;

    // A previous run's result is being replaced.
    discardOwnedImages();
    setPhase(PHASES.RUNNING);
    setErrorMessage("");
    setResult(null);

    try {
      const runResult = await runSmartImport({
        file: selectedFile,
        signal: controller.signal,
        onProgress: (p) => {
          setProgress(p);
          pushBatchNotice(p);
        }
      });
      ownedImagesRef.current = runResult.images;
      setResult(runResult);
      setPhase(PHASES.SUMMARY);
    } catch (error) {
      if (controller.signal.aborted || error.name === "AbortError") {
        setPhase(PHASES.IDLE);
        return;
      }
      setErrorMessage(
        error instanceof LlmConfigError
          ? error.message
          : error.message || "Something went wrong while importing."
      );
      setPhase(PHASES.ERROR);
    } finally {
      abortRef.current = null;
    }
  }, [selectedFile, discardOwnedImages]);

  const retryFailedBatchesRun = useCallback(async () => {
    if (!result?.failedChunks?.length) return;
    const controller = new AbortController();
    abortRef.current = controller;
    setPhase(PHASES.RUNNING);
    setErrorMessage("");
    setBatchNotices([]);
    try {
      // Keeps the questions already extracted and re-runs only the
      // failed batches. The existing images are reused as-is.
      const updated = await retryFailedBatches({
        result,
        signal: controller.signal,
        onProgress: (p) => {
          setProgress(p);
          pushBatchNotice(p);
        }
      });
      setResult(updated);
      setPhase(PHASES.SUMMARY);
    } catch (error) {
      if (controller.signal.aborted || error.name === "AbortError") {
        setPhase(PHASES.SUMMARY);
        return;
      }
      setErrorMessage(
        error.message || "Something went wrong while retrying."
      );
      setPhase(PHASES.ERROR);
    } finally {
      abortRef.current = null;
    }
  }, [result, pushBatchNotice]);

  const reviewInForm = () => {
    const flashcards = toFormikFlashcards(
      result.questions,
      result.images
    );
    // The Add form now owns these images, so stop tracking them here.
    ownedImagesRef.current = null;
    history.push(`${ROUTE_PATHS.flashcardQuestions}/Add`, {
      smartImport: {
        fileName: result.fileName,
        flashcards
      }
    });
    handleClose();
  };

  const canStart =
    phase === PHASES.IDLE && selectedFile && !configMissing;
  const isRunning = phase === PHASES.RUNNING;

  const percent = progress?.chunkCount
    ? Math.round((progress.chunkIndex / progress.chunkCount) * 100)
    : 0;

  return (
    <Modal
      title="Smart Import questions from a document"
      isOpen={isOpen}
      handleModalCancel={handleModalClose}
      maxWidth="sm"
    >
      {configMissing && (
        <Alert severity="warning" style={{ marginBottom: 12 }}>
          Smart Import is not configured. Set{" "}
          <code>REACT_APP_SMART_IMPORT_API_URL</code> to the
          extraction service URL, then rebuild the app.
        </Alert>
      )}

      {phase === PHASES.IDLE && (
        <>
          <label className={classes.fileLabel}>
            <CloudUploadIcon fontSize="large" />
            <span>
              {selectedFile
                ? selectedFile.name
                : "Choose a .docx or .pptx file"}
            </span>
            <input
              type="file"
              accept=".docx,.pptx"
              style={{ display: "none" }}
              onChange={handleFilePicked}
            />
          </label>
          <Typography className={classes.hint}>
            Questions are extracted with {getLlmModelName()} and
            prefilled into the Add form for your review. Limits:{" "}
            {IMPORT_LIMITS.maxFileSizeMB}MB, {IMPORT_LIMITS.maxImages}{" "}
            images, {IMPORT_LIMITS.maxQuestions} questions. Always
            verify the result before saving.
          </Typography>
        </>
      )}

      {isRunning && (
        <div style={{ padding: "12px 4px" }}>
          <LinearProgress variant="determinate" value={percent} />
          <Typography className={classes.progressText}>
            {progress?.phase === "parsing"
              ? "Reading the document…"
              : progress?.phase === "done"
              ? "Finishing up…"
              : progress?.chunkCount
              ? progress?.phase === "retrying"
                ? progress.message
                : `Extracting questions — batch ${
                    progress.chunkIndex + 1
                  } of ${progress.chunkCount}`
              : "Preparing extraction…"}
          </Typography>
          {batchNotices.map((notice, i) => (
            <Typography
              key={`${notice.batch}-${i}`}
              style={{
                fontSize: "0.85rem",
                color:
                  notice.message.includes("failed") &&
                  !notice.message.includes("retrying")
                    ? "#b70020"
                    : "#92400e",
                marginTop: 6
              }}
            >
              {notice.message}
            </Typography>
          ))}
        </div>
      )}

      {phase === PHASES.SUMMARY && result && (
        <>
          <Alert severity="success">
            Extracted {result.stats.total} question
            {result.stats.total === 1 ? "" : "s"} from{" "}
            <strong>{result.fileName}</strong>
            {result.stats.withImages
              ? ` (${result.stats.withImages} with images)`
              : ""}
            .
          </Alert>
          {(result.stats.inferred > 0 ||
            result.stats.unclear > 0) && (
            <Alert severity="warning" style={{ marginTop: 12 }}>
              {result.stats.inferred > 0 &&
                `${result.stats.inferred} correct answer(s) were inferred from the explanations — verify them. `}
              {result.stats.unclear > 0 &&
                `${result.stats.unclear} question(s) have no identifiable correct answer — pick one before saving.`}
            </Alert>
          )}
          {result.failedChunks.length > 0 && (
            <Alert severity="error" style={{ marginTop: 12 }}>
              {result.failedChunks.length} batch(es) failed and their
              questions are missing:{" "}
              {result.failedChunks
                .map((chunk) => `#${chunk.index}`)
                .join(", ")}
              . Use &quot;Retry failed batches&quot; to extract only
              the missing ones.
            </Alert>
          )}
          {result.truncated && (
            <Alert severity="warning" style={{ marginTop: 12 }}>
              More than {IMPORT_LIMITS.maxQuestions} questions were
              found; only the first {IMPORT_LIMITS.maxQuestions} were
              kept.
            </Alert>
          )}
          {result.textOnlyChunks > 0 && (
            <Alert severity="info" style={{ marginTop: 12 }}>
              The model could not read images directly, so document
              images were attached to question stems in source order —
              check each attachment.
            </Alert>
          )}
          <ul className={classes.summaryList}>
            <li>
              Difficulty, Mode, System and Subject are left empty —
              set them per question before saving.
            </li>
          </ul>
        </>
      )}

      {phase === PHASES.ERROR && (
        <Alert severity="error">{errorMessage}</Alert>
      )}

      <div className={classes.actions}>
        {isRunning ? (
          <Button
            text="Cancel"
            className="default"
            onClick={abortRun}
          />
        ) : phase === PHASES.SUMMARY ? (
          <>
            <Button
              text="Close"
              className="default"
              onClick={handleModalClose}
            />
            {result.failedChunks.length > 0 && (
              <Button
                text="Retry failed batches"
                className="primary-light"
                onClick={retryFailedBatchesRun}
              />
            )}
            <Button
              text="Review in form"
              className="primary"
              onClick={reviewInForm}
            />
          </>
        ) : (
          <>
            <Button
              text="Close"
              className="default"
              onClick={handleModalClose}
            />
            <Button
              text={
                phase === PHASES.ERROR ? "Retry" : "Extract questions"
              }
              className="primary"
              disabled={!canStart && phase !== PHASES.ERROR}
              onClick={
                phase === PHASES.ERROR
                  ? () => setPhase(PHASES.IDLE)
                  : startImport
              }
            />
          </>
        )}
      </div>
    </Modal>
  );
}

SmartImportModal.propTypes = {
  isOpen: PropTypes.bool,
  handleClose: PropTypes.func
};

SmartImportModal.defaultProps = {
  isOpen: false,
  handleClose: () => {}
};

export default SmartImportModal;
